import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { ShoppingBag, Plane, ArrowLeftRight } from "lucide-react";

/**
 * Iteration 11 — Unified Account Role Indicator + Switch Mode.
 *
 * Renders a BIG, color-coded pill showing the current mode:
 *   [ ShoppingBag  PEMBELI  ⇄ ]  → solid blue when buyer is active
 *   [ Plane       JASTIPER  ⇄ ]  → solid green when jastiper is active
 *
 * Clicking the pill (or the ⇄ icon) switches to the OTHER mode.
 * The switch is OPTIMISTIC: UI + theme update instantly, API call is fire-and-forget
 * (with rollback toast on failure). No perceptible delay for the user.
 */
export default function SwitchModeToggle({ compact = false, attached = false }) {
  const { user, login } = useAuth();
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const activeMode = user?.active_mode || user?.role || "buyer";

  // Don't render for admins or accounts without dual roles
  if (!user) return null;
  const hasBoth = roles.includes("buyer") && roles.includes("jastiper");
  if (!hasBoth) return null;

  const isBuyer = activeMode === "buyer";
  const nextMode = isBuyer ? "jastiper" : "buyer";

  const switchMode = async () => {
    if (busy) return;
    setBusy(true);

    // ── Optimistic update: flip state immediately so theme + label respond now.
    const prevUser = user;
    const optimisticUser = { ...user, active_mode: nextMode };
    const token = localStorage.getItem("jastip_token");
    if (token) login(token, optimisticUser);

    try {
      const r = await api.post("/auth/switch-mode", { mode: nextMode });
      if (token) login(token, r.data);
      const modeName = nextMode === "buyer" ? t("dash.mode_buyer") : t("dash.mode_jastiper");
      toast.success(
        lang === "id" ? `Mode ${modeName.toUpperCase()} aktif` : `${modeName.toUpperCase()} mode active`,
        { duration: 1600 }
      );
    } catch (e) {
      // Rollback on failure
      if (token) login(token, prevUser);
      toast.error(formatApiError(e) || "Gagal ganti mode");
    } finally {
      setBusy(false);
    }
  };

  // Color & label per mode — labels localized via i18n
  const buyerLabel = t("dash.mode_buyer").toUpperCase();
  const jastiperLabel = t("dash.mode_jastiper").toUpperCase();
  const palette = isBuyer
    ? { bg: "#002FA7", bgHover: "#001F7A", fg: "#FFFFFF", ring: "rgba(0,47,167,0.25)", label: buyerLabel, Icon: ShoppingBag }
    : { bg: "#059669", bgHover: "#047857", fg: "#FFFFFF", ring: "rgba(5,150,105,0.25)", label: jastiperLabel, Icon: Plane };

  // Compact = small icon-only pill (bottom-nav corner / very tight headers)
  if (compact) {
    return (
      <button
        type="button"
        onClick={switchMode}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 h-9 rounded-md font-mono text-[10px] font-bold tracking-wider transition-all active:scale-95 disabled:opacity-60"
        style={{
          backgroundColor: palette.bg,
          color: palette.fg,
          boxShadow: `0 0 0 3px ${palette.ring}`,
        }}
        title={`Ganti ke ${nextMode === "buyer" ? "Pembeli" : "Jastiper"}`}
        data-testid="switch-mode-compact-btn"
        data-active-mode={activeMode}
      >
        <palette.Icon size={12} strokeWidth={2.2} />
        <span data-testid="role-indicator-label-compact">{palette.label}</span>
        <ArrowLeftRight size={10} strokeWidth={2.2} style={{ opacity: 0.85 }} />
      </button>
    );
  }

  // Full prominent badge — used in header
  // When `attached`, render seamlessly INSIDE a parent grouped container:
  //   - no own rounded corners, no outer ring/shadow, no fixed height
  //   - includes a left divider in the parent's border colour so it visually "joins" the name pill
  const attachedStyles = attached
    ? {
        backgroundColor: palette.bg,
        color: palette.fg,
        borderLeft: "1px solid var(--app-border)",
      }
    : {
        backgroundColor: palette.bg,
        color: palette.fg,
        boxShadow: `0 0 0 3px ${palette.ring}, 0 2px 6px rgba(0,0,0,0.12)`,
      };

  return (
    <button
      type="button"
      onClick={switchMode}
      disabled={busy}
      className={
        attached
          ? "group inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 font-mono text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em] transition-all active:scale-95 disabled:opacity-60 flex-shrink-0"
          : "group inline-flex items-center gap-1.5 sm:gap-2 h-9 px-2 sm:px-3 rounded-md font-mono text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em] transition-all active:scale-95 disabled:opacity-60 flex-shrink-0"
      }
      style={attachedStyles}
      title={lang === "id" ? `Mode aktif: ${palette.label}. Klik untuk ganti ke ${nextMode === "buyer" ? buyerLabel : jastiperLabel}.` : `Active mode: ${palette.label}. Click to switch to ${nextMode === "buyer" ? buyerLabel : jastiperLabel}.`}
      data-testid="switch-mode-toggle"
      data-active-mode={activeMode}
      aria-label={`Active role: ${palette.label}. Click to switch mode.`}
    >
      <palette.Icon size={14} strokeWidth={2.2} />
      <span data-testid="role-indicator-label">{palette.label}</span>
      <ArrowLeftRight
        size={12}
        strokeWidth={2.2}
        style={{ opacity: 0.85 }}
        className="group-hover:rotate-180 transition-transform duration-300"
      />
    </button>
  );
}
